import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Paper,
  Stack,
  TextField,
  InputAdornment,
  MenuItem,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Search } from "@mui/icons-material";
import VistaMes from "./VistaMes";
import VistaSemana from "./VistaSemana";
import VistaDia from "./VistaDia";
import ResultSearch from "./ResultSearch";

const MONTHS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const SHORT_MONTHS = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

export default function CalendarioEvento({
  events = [],
  onDateClick,
  onEventClick,
}) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState("");
  const [view, setView] = useState("month");

  const handlePrev = () => {
    const newDate = new Date(currentDate);
    if (view === "month") {
      newDate.setMonth(currentDate.getMonth() - 1);
    } else if (view === "week") {
      newDate.setDate(currentDate.getDate() - 7);
    } else {
      newDate.setDate(currentDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (view === "month") {
      newDate.setMonth(currentDate.getMonth() + 1);
    } else if (view === "week") {
      newDate.setDate(currentDate.getDate() + 7);
    } else {
      newDate.setDate(currentDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const isToday = (d, m, y) => {
    const today = new Date();
    return (
      d === today.getDate() &&
      m === today.getMonth() &&
      y === today.getFullYear()
    );
  };

  const getStartOfWeek = (date) => {
    const start = new Date(date);
    const day = start.getDay();
    start.setDate(start.getDate() - (day === 0 ? 6 : day - 1));
    return start;
  };

  const getHeaderTitle = () => {
    if (view === "day") {
      return `${currentDate.getDate()} de ${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    }

    if (view === "week") {
      const start = getStartOfWeek(currentDate);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);

      if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
        return `${MONTHS[start.getMonth()]} ${start.getFullYear()}`;
      }

      if (start.getFullYear() === end.getFullYear()) {
        return `${SHORT_MONTHS[start.getMonth()]} - ${SHORT_MONTHS[end.getMonth()]} ${start.getFullYear()}`;
      }

      return `${SHORT_MONTHS[start.getMonth()]} ${start.getFullYear()} - ${SHORT_MONTHS[end.getMonth()]} ${end.getFullYear()}`;
    }

    return `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  };

  const renderView = () => {
    if (searchTerm.trim() !== "") {
      return (
        <ResultSearch 
          events={events} 
          searchTerm={searchTerm} 
          onEventClick={onEventClick} 
        />
      );
    }

    const commonProps = {
      currentDate,
      events,
      searchTerm,
      onEventClick,
      isToday,
    };

    switch (view) {
      case "day":
        return <VistaDia {...commonProps} />;
      case "week":
        return <VistaSemana {...commonProps} onDateClick={onDateClick} />;
      case "month":
      default:
        return <VistaMes {...commonProps} onDateClick={onDateClick} />;
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 0,
        border: 0,
        borderRadius: 0,
        bgcolor: "transparent",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        minHeight: 0,
        flex: 1,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: "#606071",
          px: 1,
          py: 0.5,
          borderTopLeftRadius: "6px",
          borderTopRightRadius: "6px",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleToday}
              sx={{
                textTransform: "none",
                color: "text.primary",
                borderColor: "rgba(0, 0, 0, 0.23)",
                bgcolor: "background.paper",
                borderRadius: "4px",
                fontWeight: 500,
                px: 2,
                py: 0.8,
                minWidth: 60,
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              Hoy
            </Button>
            <Stack direction="row" spacing={0}>
              <IconButton onClick={handlePrev} size="small" sx={{ color: "white" }}>
                <ChevronLeft />
              </IconButton>
              <IconButton onClick={handleNext} size="small" sx={{ color: "white" }}>
                <ChevronRight />
              </IconButton>
            </Stack>
            <Typography
              sx={{ fontWeight: 400, minWidth: { xs: 170, sm: 210 }, color: "white", textAlign: "center", fontSize: { xs: 18, sm: 20 } }}
            >
              {getHeaderTitle()}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <TextField
              size="small"
              placeholder="Busca por nombre de evento..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: 180, sm: 250 },
                bgcolor: "background.paper",
                borderRadius: "4px",
                "& .MuiOutlinedInput-root": { borderRadius: "4px" },
                "& .MuiInputBase-input": { fontSize: 14, py: 0.65 },
              }}
            />

            <TextField
              select
              size="small"
              value={view}
              onChange={(e) => setView(e.target.value)}
              sx={{
                minWidth: 80,
                bgcolor: "background.paper",
                borderRadius: "4px",
                "& .MuiOutlinedInput-root": { borderRadius: "4px" },
                "& .MuiSelect-select": { fontSize: 14, py: 0.5 },
              }}
            >
              <MenuItem value="day">Día</MenuItem>
              <MenuItem value="week">Semana</MenuItem>
              <MenuItem value="month">Mes</MenuItem>
            </TextField>
          </Stack>
        </Stack>
      </Box>

      {/* Content Area */}
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
        {renderView()}
      </Box>
    </Paper>
  );
}
